import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "../../../components/breadcrumb/Breadcrumb";
import useGroupeCategorie from "../../../hooks/useGroupeCategorie";
import "../../../assets/css/admin/gestionGroupCategorie/AdminGroupeCategoriesPage.css";

export default function AdminGroupeCategoriesPage({ setAlert }) {
  const { groupes, loading, fetchGroupes, createGroupe, updateGroupe, deleteGroupe } =
    useGroupeCategorie();

  // UI state
  const [q, setQ] = useState("");
  const [archive, setArchive] = useState("all"); // all | 2 | 1
  const [type, setType] = useState("all"); // all | 1 | 2
  const [sort, setSort] = useState("newest"); // newest | oldest | nom_asc | nom_desc | rang_asc | rang_desc
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // modal form
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const emptyForm = {
    nom: "",
    type: 2,     // 1 premium, 2 standard
    archive: 2,  // 1 archivé, 2 non archivé
    rang: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const breadcrumbItems = [
    { label: "Mon Compte", link: "/utilisateur/compte" },
    { label: "Admin", link: "/admin/dashboard" },
    { label: "Groupes Catégories" },
  ];

  useEffect(() => {
    fetchGroupes().catch(() => {
      setAlert?.({ type: "danger", message: "Impossible de charger les groupes catégories." });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (g) => {
    setEditId(g?._id);
    setForm({
      nom: g?.nom ?? "",
      type: Number(g?.type ?? 2),
      archive: Number(g?.archive ?? 2),
      rang: g?.rang ?? "",
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
  };

  const typeLabel = (t) => (Number(t) === 1 ? "Premium" : "Standard");
  const archiveLabel = (a) => (Number(a) === 1 ? "Archivé" : "Actif");

  const filtered = useMemo(() => {
    const nq = String(q || "").toLowerCase().trim();

    return [...(groupes || [])]
      .filter((g) => {
        const okQ = !nq || String(g?.nom || "").toLowerCase().includes(nq);
        const okArchive = archive === "all" ? true : Number(g?.archive) === Number(archive);
        const okType = type === "all" ? true : Number(g?.type) === Number(type);
        return okQ && okArchive && okType;
      })
      .sort((a, b) => {
        const da = new Date(a?.createdAt || 0).getTime();
        const db = new Date(b?.createdAt || 0).getTime();

        if (sort === "newest") return db - da;
        if (sort === "oldest") return da - db;

        const na = String(a?.nom || "").toLowerCase();
        const nb = String(b?.nom || "").toLowerCase();
        if (sort === "nom_asc") return na.localeCompare(nb);
        if (sort === "nom_desc") return nb.localeCompare(na);

        const ra = Number(a?.rang ?? 0);
        const rb = Number(b?.rang ?? 0);
        if (sort === "rang_asc") return ra - rb;
        if (sort === "rang_desc") return rb - ra;

        return 0;
      });
  }, [groupes, q, archive, type, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  useEffect(() => {
    setPage(1);
  }, [q, archive, type, sort]);

  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page]);

  const onSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      nom: String(form.nom || "").trim(),
      type: Number(form.type),
      archive: Number(form.archive),
      rang: form.rang === "" ? undefined : Number(form.rang),
    };

    if (!payload.nom) {
      setAlert?.({ type: "warning", message: "Le champ 'nom' est requis." });
      return;
    }
    if (payload.rang != null && !Number.isFinite(payload.rang)) {
      setAlert?.({ type: "warning", message: "Le champ 'rang' doit être un nombre." });
      return;
    }

    setSaving(true);
    try {
      if (editId) {
        await updateGroupe(editId, payload);
        setAlert?.({ type: "success", message: "Groupe modifié avec succès." });
      } else {
        await createGroupe(payload);
        setAlert?.({ type: "success", message: "Groupe créé avec succès." });
      }
      setModalOpen(false);
      setForm(emptyForm);
      setEditId(null);
    } catch (err) {
      console.error(err);
      setAlert?.({ type: "danger", message: "Échec de l’enregistrement." });
    } finally {
      setSaving(false);
    }
  };

  const onToggleArchive = async (g) => {
    const next = Number(g?.archive) === 1 ? 2 : 1;
    try {
      await updateGroupe(g?._id, { archive: next });
      setAlert?.({
        type: "success",
        message: next === 1 ? "Groupe archivé." : "Groupe réactivé.",
      });
    } catch (err) {
      console.error(err);
      setAlert?.({ type: "danger", message: "Impossible de changer l’archive." });
    }
  };

  const onDelete = async (g) => {
    const ok = window.confirm(`Supprimer le groupe "${g?.nom || ""}" ?`);
    if (!ok) return;

    try {
      await deleteGroupe(g?._id);
      setAlert?.({ type: "success", message: "Groupe supprimé." });
    } catch (err) {
      console.error(err);
      setAlert?.({ type: "danger", message: "Suppression impossible." });
    }
  };

  return (
    <div className="adminGroupPage">
      <div className="container">
        <Breadcrumb items={breadcrumbItems} />

        <div className="adminGroupHeader">
          <div>
            <div className="adminGroupTitle">Groupes Catégories</div>
            <div className="adminGroupSubtitle">
              {loading ? "Chargement..." : "Gérer les groupes (premium/standard), rang et archivage."}
            </div>
          </div>

          <div className="adminGroupActions">
            <button
              className="adminBtn adminBtnSoft"
              type="button"
              onClick={() => fetchGroupes()}
              disabled={loading}
              title="Rafraîchir"
            >
              <i className="bi bi-arrow-clockwise" /> Rafraîchir
            </button>

            <button className="adminBtn adminBtnPrimary" type="button" onClick={openCreate}>
              <i className="bi bi-plus-circle" /> Nouveau groupe
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="adminGroupFilters">
          <div className="filterItem">
            <label>Recherche</label>
            <input
              className="form-control"
              placeholder="Rechercher par nom…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          <div className="filterItem">
            <label>Archive</label>
            <select className="form-select" value={archive} onChange={(e) => setArchive(e.target.value)}>
              <option value="all">Tous</option>
              <option value="2">Actifs (non archivés)</option>
              <option value="1">Archivés</option>
            </select>
          </div>

          <div className="filterItem">
            <label>Type</label>
            <select className="form-select" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="all">Tous</option>
              <option value="1">Premium</option>
              <option value="2">Standard</option>
            </select>
          </div>

          <div className="filterItem">
            <label>Tri</label>
            <select className="form-select" value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="newest">Plus récent</option>
              <option value="oldest">Plus ancien</option>
              <option value="nom_asc">Nom (A→Z)</option>
              <option value="nom_desc">Nom (Z→A)</option>
              <option value="rang_asc">Rang (↑)</option>
              <option value="rang_desc">Rang (↓)</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="adminGroupTableWrap">
          {loading ? (
            <div className="adminEmpty">Chargement…</div>
          ) : filtered.length === 0 ? (
            <div className="adminEmpty">Aucun groupe trouvé.</div>
          ) : (
            <table className="table adminCategoriesTable">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th className="hideOnMobile">Type</th>
                  <th className="hideOnMobile">Rang</th>
                  <th className="textCenter">Statut</th>
                  <th className="colActions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((g) => {
                  // const a -> archive, t -> type, g -> groupe
                  const a = Number(g?.archive ?? 2);
                  const t = Number(g?.type ?? 2);

                  return (
                    <tr key={g?._id}>
                      <td>
                        <div className="rowTitle">{g?.nom || "—"}</div>
                      </td>

                      <td>
                        <span className={`pill ${t === 1 ? "pillPremium" : "pillStandard"}`}>
                          {typeLabel(t)}
                        </span>
                      </td>

                      <td>
                        <span className="pill pillNeutral">{g?.rang ?? "—"}</span>
                      </td>

                      <td>
                        <span className={`pill ${a === 1 ? "pillDanger" : "pillGood"}`}>
                          {archiveLabel(a)}
                        </span>
                      </td>

                      <td>
                        <div className="rowActions">
                          <button className="btnAction" onClick={() => openEdit(g)} title="Modifier">
                            <i className="bi bi-pencil-square" /> 
                          </button>

                          <button className="btnAction" onClick={() => onToggleArchive(g)} title="Archiver / Réactiver">
                            <i className={`bi ${a === 1 ? "bi-arrow-counterclockwise" : "bi-archive"}`} />{" "}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {filtered.length > 0 && (
          <div className="adminPagination">
            <button
              className="adminBtn adminBtnSoft"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <i className="bi bi-chevron-left" /> Précédent
            </button>

            <div className="pageInfo">
              Page <strong>{page}</strong> / {totalPages} • {filtered.length} résultat(s)
            </div>

            <button
              className="adminBtn adminBtnSoft"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Suivant <i className="bi bi-chevron-right" />
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="adminModalOverlay" onClick={closeModal}>
          <div className="adminModal" onClick={(e) => e.stopPropagation()}>
            <div className="adminModalHeader">
              <div className="adminModalTitle">
                {editId ? "Modifier un groupe" : "Créer un groupe"}
              </div>
              <button className="adminModalClose" onClick={closeModal} disabled={saving} title="Fermer">
                <i className="bi bi-x-lg" />
              </button>
            </div>

            <form onSubmit={onSubmit}>
              <div className="adminModalBody">
                <div className="mb-3">
                  <label className="form-label fw-bold">Nom</label>
                  <input
                    className="form-control"
                    value={form.nom}
                    onChange={(e) => setForm((p) => ({ ...p, nom: e.target.value }))}
                    placeholder="Ex: Immobilier"
                  />
                </div>

                <div className="row g-3">
                  <div className="col-12 col-md-4">
                    <label className="form-label fw-bold">Type</label>
                    <select
                      className="form-select"
                      value={form.type}
                      onChange={(e) => setForm((p) => ({ ...p, type: Number(e.target.value) }))}
                    >
                      <option value={1}>Premium</option>
                      <option value={2}>Standard</option>
                    </select>
                  </div>

                  <div className="col-12 col-md-4">
                    <label className="form-label fw-bold">Archive</label>
                    <select
                      className="form-select"
                      value={form.archive}
                      onChange={(e) => setForm((p) => ({ ...p, archive: Number(e.target.value) }))}
                    >
                      <option value={2}>Actif</option>
                      <option value={1}>Archivé</option>
                    </select>
                  </div>

                  <div className="col-12 col-md-4">
                    <label className="form-label fw-bold">Rang</label>
                    <input
                      className="form-control"
                      value={form.rang}
                      onChange={(e) => setForm((p) => ({ ...p, rang: e.target.value }))}
                      placeholder="Ex: 1"
                      inputMode="numeric"
                    />
                  </div>
                </div>
              </div>

              <div className="adminModalFooter">
                <button type="button" className="adminBtn adminBtnSoft" onClick={closeModal} disabled={saving}>
                  Annuler
                </button>

                <button type="submit" className="adminBtn adminBtnPrimary" disabled={saving}>
                  {saving ? "Enregistrement…" : editId ? "Modifier" : "Créer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
